import React, { FunctionComponent } from 'react';
import { Answers, ImageDiagnosisField, ImageDiagnosisFieldSchema } from '../dynamicFormSchema';
import {
  ImageDiagnosis,
  DiagnosisImageSets,
  DiagnosisOptionSets,
  DiagnosisOptionSetName,
  DiagnosisImageSetName,
  ImageDiagnosisValues,
  ImageDiagnosisValuesSchema
} from '../../ImageDiagnosis';
import { DynamicFieldProps } from '../DynamicField';
import { DynamicFieldComponent } from './DynamicFieldComponentBase';
import { TypeConverterName, typeConverters } from '../typeConverters';

export const ImageDiagnosisFieldComponent: FunctionComponent<DynamicFieldProps> = ({
  field,
  reactHookField,
  onChange
}) => {
  const dp = ImageDiagnosisFieldSchema.parse(field);

  return (
    <ImageDiagnosis
      label={dp.label}
      name={dp.name}
      values={reactHookField.value}
      disabled={reactHookField.disabled}
      initialOptionValue={dp.defaultOptionValue}
      diagnosisImages={DiagnosisImageSets[dp.diagnosisImageSet as DiagnosisImageSetName]}
      options={DiagnosisOptionSets[dp.diagnosisOptionSet as DiagnosisOptionSetName]}
      onChange={onChange}
    />
  );
};

export class DynamicImageDiagnosisField extends DynamicFieldComponent<ImageDiagnosisField, ImageDiagnosisValues> {
  Component = ImageDiagnosisFieldComponent;

  constructor(field: ImageDiagnosisField) {
    super(field);
  }

  getAnswer(answers: Answers): ImageDiagnosisValues {
    const answer = answers[this.field.name];
    if (answer === undefined) {
      return this.getDefaultValue();
    }

    if (ImageDiagnosisValuesSchema.safeParse(answer).success) {
      const answerValues = answer as ImageDiagnosisValues;

      // If the field type is boolean, convert boolean values back to their numeric representation
      // (the reverse of what getValueToSubmit does)
      if (this.field?.fieldType === 'boolean') {
        const convertedValues: ImageDiagnosisValues = {};
        for (const key in answerValues) {
          const value = answerValues[key];
          // Convert boolean back to number: true -> 1, false -> 0
          convertedValues[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value;
        }
        return { ...this.getDefaultValue(), ...convertedValues };
      }

      // Merge in default answers for any missing points
      return { ...this.getDefaultValue(), ...answerValues };
    }

    throw new Error(`Invalid answer for field ${this.field.name}`);
  }

  getDefaultValue(): ImageDiagnosisValues {
    const imageSet = DiagnosisImageSets[this.field.diagnosisImageSet as DiagnosisImageSetName];
    const defaultValues: ImageDiagnosisValues = {};
    imageSet.forEach((img) => {
      if (img.points) {
        img.points.forEach((point) => {
          defaultValues[point.name] = this.field.defaultOptionValue as number | string;
        });
      }
    });
    return defaultValues;
  }

  getValueToSubmit(value: ImageDiagnosisValues): ImageDiagnosisValues {
    // If the value is empty, then use the default value.
    if (!value) {
      value = this.getDefaultValue();
    }

    // Convert the values to the correct type. Default to number if no type is specified.
    const converter = typeConverters[(this.field?.fieldType || 'number') as TypeConverterName];
    const convertedValues: ImageDiagnosisValues = {};
    for (const key in value) {
      convertedValues[key] = converter(value[key]);
    }
    return convertedValues;
  }
}
